// TOSPage.tsx
//
// The Terms, and the plain-language half of the threat-model statement
// (takenote_decisions.md 10.1, which has always required it "in the
// requirements and, in plain language, on the TOS page").
//
// ---------------------------------------------------------------------------
// A TERMS PAGE IS THE WORST PLACE TO CARRY A STALE SENTENCE. A user cannot
// check it against the code, so it is believed. Every fact below was checked
// against the code when it was written; when the code changes, change this
// page in the same commit. Past mistakes this page made, so they are not made
// again:
//
//   - Kept notes "1 month if unretrieved" and "soft-deleted for 365 days" -
//     both described designs that were refused (9.3.1, 9.5).
//   - "No account recovery if PIN is lost" - backwards. The lock screen's
//     "Forgot your PIN? Use your passphrase instead" unlocks with the
//     passphrase and sets a new PIN in the same step (6.5). Losing the
//     PASSPHRASE is what cannot be recovered.
//   - "Even complete control of the server cannot read your notes" - too
//     strong. The server is the only source of other users' public keys
//     (/users/resolve) and the extension does not pin or verify them, so a
//     taken-over server could hand out a false key. Section 3 says so.
//   - "Deleting removes everything in the same moment" - some traces last for
//     a bounded time (audit rows, doorbell pings, platform backups and logs).
//   - "The account password does not protect your notes" - it controls who can
//     move the account to a new browser, and so who receives future notes.
//
// THIS IS NOT LEGAL ADVICE. Anything that must be confirmed before launch is
// marked TODO below; there is nothing marked at the moment.
//
// THE DATE BELOW IS HAND-WRITTEN AND TRACKS TOS_VERSION (lib/tos.ts), which
// `tosAcceptance()` writes into a new user's metadata and the
// before_user_created hook checks against `public.current_tos_version()`
// (9.6). All three were set ONCE, to the launch text, on 2026-10-22 (Phase 16;
// the SQL side is the migration 20261022_250_phase16_tos_version.sql). Moving
// one means moving all three in the same commit, and `pnpm test:landing` holds
// them equal from the files.
// ---------------------------------------------------------------------------

import BrandHeader from "../lib/BrandHeader";

export default function TOSPage() {
  return (
    <div className="min-h-screen bg-canvas">
      <div className="mx-auto max-w-3xl px-6 py-10">
        <div className="rounded-xl border border-line bg-surface p-6 shadow-sm">
          <BrandHeader />
          <h1 className="mb-1 text-xl font-semibold text-fg-strong">Terms and privacy</h1>
          <p className="text-sm text-fg-soft">
            Last updated 22 October 2026. Please read these terms carefully, especially
            section 3, which explains what TakeNote protects and what it does not.
          </p>

          <div className="mt-6 space-y-6 text-sm leading-relaxed text-fg-body">
            {/*
              The privacy summary (2026-10-12). This page is also TakeNote's privacy policy - the
              address the Chrome Web Store asks for - so it opens with a short summary that points
              into the sections carrying the detail. Everything in it is restated from sections 3,
              4, 7 and 8; `test:landing` holds the company names and the section headings equal.
            */}
            <section id="privacy">
              <h2 className="text-base font-semibold text-fg-strong">Privacy in short</h2>
              <p className="mt-2">
                This page is also TakeNote's privacy policy. The detail is in sections 3, 4, 7 and 8; in
                short:
              </p>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                <li>
                  Your notes are encrypted on your device. Our server only ever stores sealed notes.
                  Section 3 says what that protects and what it does not.
                </li>
                <li>
                  We hold your email address, your username (encrypted with a key our server holds), your
                  public keys, who receives each note, when, and how big it is, and two daily counts. Your
                  IP address and browser details are in our providers' logs for about a week.
                </li>
                <li>
                  Supabase (database, sign-in and server code), Cloudflare (web pages and a human check),
                  Resend (email), Have I Been Pwned (a check against leaked passwords) and Google (the Chrome
                  Web Store) handle parts of the service for us.
                </li>
                <li>
                  A note is deleted from our server once your device confirms it has it, or after about 7
                  days if it is never collected. Deleting your account removes your account, keys and
                  waiting notes at once.
                </li>
                <li>
                  You can reach us at{" "}
                  <a className="text-link hover:underline" href="mailto:support@takenote.fyi">
                    support@takenote.fyi
                  </a>
                  .
                </li>
              </ul>
            </section>

            {/* Operator: one person, named by the owner on 2026-10-12, reached at the contact address in section 15. No postal address. */}
            <section>
              <h2 className="text-base font-semibold text-fg-strong">1. Who we are, and what TakeNote is</h2>
              <p className="mt-2">
                TakeNote (takenote.fyi) is run by its developer, Sheldon Hall. In these terms, "we" and "us"
                mean that operator.
              </p>
              <p className="mt-3">
                "TakeNote" means three things together: the browser extension, the web pages at
                app.takenote.fyi (sign up, log in, password reset, account deletion and this
                page), and the server behind them.
              </p>
            </section>

            {/* Minimum age 16: confirmed by the owner, 2026-10-12. */}
            <section>
              <h2 className="text-base font-semibold text-fg-strong">2. Accepting these terms</h2>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                <li>By creating an account and using TakeNote, you agree to these terms.</li>
                <li>You must be at least 16 years old to use TakeNote.</li>
                <li>
                  When you sign up, we record which version of these terms you accepted and
                  when. The time comes from your own browser's clock. This record is kept with
                  your account and deleted with it.
                </li>
              </ul>
            </section>

            {/*
              10.1, in plain language. It is deliberately honest about what is
              NOT protected: a threat model that names its own limits is worth
              more than a partial privacy feature, and every limit below is one
              this system actually has.
            */}
            <section>
              <h2 className="text-base font-semibold text-fg-strong">
                3. What TakeNote protects, and what it does not
              </h2>

              <p className="mt-2">
                <strong>Your notes are encrypted on your device.</strong> Encrypted means
                scrambled so that only the right key can turn it back into words. Each account
                has two kinds of keys: <strong>private keys</strong>, which open notes and stay
                on your device, and <strong>public keys</strong>, which let other people seal
                notes to you and are stored on our server. A note is sealed on your device
                before it is sent, and our server only ever stores sealed notes. Someone who
                gets a copy of our database or our backups cannot read them.
              </p>

              <p className="mt-3">
                <strong>What that protection relies on.</strong> Two things must be true: the
                extension you installed is the genuine one, and the public keys our server hands
                out are real. When you send a note to a username, our server tells your
                extension which public key belongs to that name, and the extension has no other
                way to check it. So if an attacker took over our server, they could hand out a
                false key, read notes sent after that, or make a note appear to come from someone it did not. They could not read notes sent before
                it. We publish the extension and run the server, so the same limits apply to
                us.
              </p>

              <p className="mt-3">
                <strong>Who you talk to is not hidden.</strong> The sender's name is sealed
                inside the note, so a stored note does not say who sent it. But you are signed
                in when you send, so the running service does see who is sending. Who receives
                each note, when, and how big it is are visible to our server.
              </p>

              <p className="mt-3">
                <strong>Daily counts.</strong> To limit abuse, we count two things per account
                per day: how many notes you sent, and how many username lookups failed. We never
                record who you sent to or which names you looked up. Even so, a copy of our
                database shows which days you sent notes and how many. Where few people send on
                the same day, that can narrow down, or even reveal, who sent a given note.
              </p>

              <p className="mt-3">
                <strong>What our server knows about you:</strong>
              </p>
              <ul className="mt-1 list-disc space-y-1 pl-5">
                <li>your email address;</li>
                <li>
                  your username (stored encrypted with a key our server holds, so our server can
                  read it);
                </li>
                <li>your public keys;</li>
                <li>
                  your IP address (your network address on the internet) and browser details,
                  in our providers' logs for about a week;
                </li>
                <li>who receives each note, when, and its size;</li>
                <li>the daily counts above.</li>
              </ul>
            </section>

            {/*
              Decided 2026-10-12: this page is the privacy policy (see the summary at the top), so
              there is no separate Privacy Policy page to write.
            */}
            <section>
              <h2 className="text-base font-semibold text-fg-strong">4. Other companies that handle your data</h2>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                <li>
                  <strong>Supabase</strong> runs our database, sign-in and server code, in the
                  United States (Amazon's us-west-2 region). It sees your email address, your IP
                  address and every request the extension and web pages make.
                </li>
                <li>
                  <strong>Cloudflare</strong> hosts the web pages and forwards email sent to
                  support@takenote.fyi. It also runs a human check (Turnstile) on the sign-up,
                  log-in, resend verification email, forgot password and delete account pages.
                  For that check, Cloudflare sees that a browser at your IP address is trying
                  one of those things. It is not given your email address and never sees your
                  password. Sending and reading notes do not involve Cloudflare.
                </li>
                <li>
                  <strong>Resend</strong> sends our emails. It sees your email address and what
                  the email says, such as a confirmation or password reset link.
                </li>
                <li>
                  <strong>Have I Been Pwned</strong> (Pwned Passwords, run with Cloudflare)
                  helps us refuse passwords that have leaked in past data breaches. When you set
                  a password, our server, never your browser, sends it the first 5 characters
                  of the password's SHA-1 code. (SHA-1 turns text into a fixed code that cannot
                  be turned back.) It never receives the password itself.
                </li>
                <li>
                  <strong>Google</strong> installs and updates the extension through the Chrome
                  Web Store. TakeNote does not use Chrome sync.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-base font-semibold text-fg-strong">5. Your keys, and what we cannot recover</h2>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                <li>
                  Your <strong>private keys</strong> are created on your device and never leave
                  it. We do not hold a copy, so we cannot read your notes and cannot restore
                  them for you.
                </li>
                <li>
                  Your <strong>passphrase</strong> protects your keys on this device. If you
                  forget it, no one can recover or reset it, and the notes on this device cannot
                  be read again. "Reset this device" lets you start over: you keep your account
                  and username, but the keys and notes on that browser are removed. This is on
                  purpose, not a gap we plan to close.
                </li>
                <li>
                  Your <strong>PIN</strong> unlocks TakeNote quickly during a browser session. It is a lock against someone at your keyboard, not against software that can read the memory of your browser.
                  Losing it is not serious: on the lock screen, choose "Forgot your PIN? Use
                  your passphrase instead", then unlock with your passphrase and set a new PIN.
                </li>
                <li>
                  Your <strong>account password</strong> signs you in, and it matters. Anyone
                  who has it, or who controls your email and uses it to reset the password, can
                  move your account to their own browser. From then on they would receive your
                  new notes and could send notes as you. They could not read notes already on
                  your device. Use a strong password you do not use anywhere else, and keep your
                  email account safe.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-base font-semibold text-fg-strong">6. One browser at a time</h2>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                <li>
                  Your account is active on one browser at a time. Your keys exist only where you
                  created them, and nothing is synced.
                </li>
                <li>
                  You can move your account to a new browser. When you do, the old browser is
                  signed out, and notes on it, and notes on their way to it, are lost.
                </li>
                <li>
                  Removing the extension or clearing your browser's data deletes your keys and
                  the notes on that browser for good.
                </li>
                <li>
                  Your contacts list (names you have sent notes to) is kept only on your
                  browser, until you remove it.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-base font-semibold text-fg-strong">7. How long things are kept</h2>
              <p className="mt-2">
                Clean-up runs once a day, so each time below can run up to about a day longer.
              </p>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                <li>
                  <strong>A note you receive is deleted from our server as soon as your device
                  confirms it has it.</strong> We do not keep a copy.
                </li>
                <li>A note that is never collected is deleted about 7 days after it was sent.</li>
                <li>Notes on your device are removed after 7 days.</li>
                <li>The daily counts in section 3 are deleted after 2 days.</li>
                <li>
                  Once a note is on the recipient's device, what they do with it is up to them.
                  Nothing we do can limit that.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-base font-semibold text-fg-strong">8. Deleting your account</h2>
              <p className="mt-2">
                You can delete your account at any time. In the extension, open Settings and
                choose Delete account. This opens a page on app.takenote.fyi where you confirm
                with your password. The extension carries out the deletion, so it must be
                installed on the browser where your account is active.
              </p>
              <p className="mt-3">
                <strong>Removed at once:</strong> your account, profile, keys, notes waiting for
                you, your acceptance of these terms and your daily counts. You are signed out
                straight away, and the keys and notes on your browser are wiped.
              </p>
              <p className="mt-3">
                <strong>Left behind for a limited time:</strong>
              </p>
              <ul className="mt-1 list-disc space-y-1 pl-5">
                <li>
                  activity-log entries, with your account removed from them, deleted within about
                  7 days. Entries where you were the recipient of a note still carry your
                  account's ID number until then;
                </li>
                <li>short-lived delivery alerts, gone within about 75 minutes;</li>
                <li>Supabase's backups and platform logs, for about a week.</li>
              </ul>
              <p className="mt-3">
                Notes you already sent still reach their recipients.
              </p>
              <p className="mt-3">
                <strong>Kept forever: your username, as a one-way code.</strong> We turn the
                name into a code using a secret key only our server holds. The code cannot be
                turned back into your name, but we can check whether a guessed name matches it.
                It is linked to nothing else. It exists so nobody can take your name later and
                send notes that appear to be from you.
              </p>
            </section>

            <section>
              <h2 className="text-base font-semibold text-fg-strong">9. Limits</h2>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                <li>50 notes sent per day.</li>
                <li>20 failed username lookups per day.</li>
                <li>
                  Daily limits reset at midnight UTC (Coordinated Universal Time, the world's
                  reference clock).
                </li>
                <li>
                  4,000 characters per note. Some languages, such as Chinese, Japanese and
                  Korean, reach the size limit a little sooner.
                </li>
              </ul>
              <p className="mt-2">We may change these limits.</p>
            </section>

            <section>
              <h2 className="text-base font-semibold text-fg-strong">10. Acceptable use</h2>
              <p className="mt-2">Do not use TakeNote to:</p>
              <ul className="mt-1 list-disc space-y-1 pl-5">
                <li>send illegal content;</li>
                <li>harass, threaten or abuse anyone;</li>
                <li>send spam, or bulk or automated messages;</li>
                <li>attack, probe or overload the service;</li>
                <li>pretend to be someone else.</li>
              </ul>
              <p className="mt-2">
                We may limit, suspend or delete accounts that break these rules. Because we
                cannot read notes, we act on reports and on what we can see, such as how an
                account is being used.
              </p>
            </section>

            <section>
              <h2 className="text-base font-semibold text-fg-strong">11. No guarantees</h2>
              <p className="mt-2">
                TakeNote is free and provided "as is", without any warranty. We do not promise
                it will always be available or free of errors.
              </p>
              <p className="mt-3">
                Notes can be lost for good: if you forget your passphrase, when notes expire,
                when you move browsers, or when browser data is cleared. Keep your own copy of
                anything important.
              </p>
            </section>

            <section>
              <h2 className="text-base font-semibold text-fg-strong">12. Limitation of liability</h2>
              <p className="mt-2">
                To the extent the law allows, we are not liable for any loss or damage from
                using TakeNote, or from being unable to use it, including lost notes.
              </p>
            </section>

            <section>
              <h2 className="text-base font-semibold text-fg-strong">13. Changes to these terms</h2>
              <p className="mt-2">
                We will post changes on this page with a new date. For important changes, we
                will also tell you in the extension or by email. If you keep using TakeNote after
                a change, you accept the new terms. If you do not, you can delete your account
                at any time.
              </p>
            </section>

            {/* Governing law chosen by the owner, 2026-10-12: England and Wales. */}
            <section>
              <h2 className="text-base font-semibold text-fg-strong">14. Governing law</h2>
              <p className="mt-2">
                These terms are governed by the laws of England and Wales. If a dispute cannot be
                settled directly, the courts of England and Wales can hear it. Nothing in these
                terms takes away rights you have under consumer protection law where you live,
                including the right to bring a claim in your own country's courts where that law
                gives it to you.
              </p>
            </section>

            <section>
              <h2 className="text-base font-semibold text-fg-strong">15. Contact</h2>
              <p className="mt-2">
                Email:{" "}
                <a className="underline hover:text-ink" href="mailto:support@takenote.fyi">
                  support@takenote.fyi
                </a>
              </p>
            </section>
          </div>

          {/*
            The follow-up named in PR #89 ("a link from the Terms page"). Outside the numbered
            sections on purpose (test:landing holds this): a link here changes neither what a person
            agrees to nor which version of the terms they accepted. The words are the security page's
            own title. It opens in this tab: the sign-up page's Terms link opens a new one because a
            half-filled form is behind it, and there is no form here to lose.
          */}
          <div className="mt-8 text-xs text-fg-hint">
            takenote.fyi
            {" · "}
            <a className="underline hover:text-ink" href="https://takenote.fyi/security/">
              How TakeNote protects your notes
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
